import { useReducer } from 'react';

import useHotelReviewsFetch from './useHotelReviewsFetch';

import type { HotelReviewsState, ReviewsContext } from '../types/hotel-reviews';
import type { UBFFGateway } from '@shared/types/frontend/ubff-gateway';

import { REVIEWS_PER_PAGE_MOBILE } from '@hotels-hotel-detail/utils/review-service';

const reducer = (
  state: HotelReviewsState,
  update: Partial<HotelReviewsState>,
): HotelReviewsState => ({
  ...state,
  ...update,
});

const useHotelReviewsStore = (
  hotelId: string,
  locale: string,
  uBFFGateway: UBFFGateway,
): ReviewsContext => {
  const [state, setState] = useReducer(reducer, {
    hotelId,
    locale,
    isLoading: true,
    isInitialized: false,
    filters: [],
    offset: 0,
  });

  const { fetchData } = useHotelReviewsFetch(state, setState, uBFFGateway);

  return {
    state,
    actions: {
      applyFilter: (selectedFilter) => {
        const otherFilters = state.filters?.filter(
          (filter) => filter.type !== selectedFilter.type,
        );

        setState({
          filters:
            selectedFilter.value === ''
              ? otherFilters
              : [...(otherFilters ?? []), selectedFilter],
          offset: 0,
        });
      },
      applySortBy: (selectedSortType) => {
        setState({
          sortType: selectedSortType ?? state.sortType,
          offset: 0,
        });
      },
      fetchOffset: (offset) => {
        setState({ offset });
      },
      setHotelId: (newHotelId) => {
        setState({ hotelId: newHotelId, offset: 0 });
      },
      reload: () => {
        setState({ offset: 0 });
      },
      fetchData: async (offset) => {
        const { error, response } = await fetchData(
          offset,
          REVIEWS_PER_PAGE_MOBILE,
        );
        if (error) {
          throw error;
        }
        return response?.reviews;
      },
    },
  };
};

export default useHotelReviewsStore;
